import React from 'react';
import {ConsultationGroup} from '@/constants';
import {cn} from '@/utils/common_functions';

interface TabButtonsProps {
  activeTab: ConsultationGroup;
  onTabChange: (tab: ConsultationGroup) => void;
}

interface Tab {
  type: ConsultationGroup;
  text: string;
}

export const TabButtons: React.FC<TabButtonsProps> = ({
  activeTab,
  onTabChange,
}) => {
  const Tabs: Tab[] = [
    {
      type: ConsultationGroup.Ongoing,
      text: 'Ongoing Consultations',
    },
    {
      type: ConsultationGroup.Past,
      text: 'Past Consultations',
    },
    {
      type: ConsultationGroup.ConsultationHistory,
      text: 'Consultation History',
    },
  ];

  // Returns either a single string (for one-word labels) or an object with firstLine/secondLine
  const getTextForSmall = (
    text: string,
  ): string | {firstLine: string; secondLine: string} => {
    const words = text.trim().split(/\s+/);
    if (words.length === 1) return text;

    // For two-word labels: split as firstWord / secondWord (so "Ongoing Consultation" -> "Ongoing" / "Consultation")
    if (words.length === 2) {
      return {firstLine: words[0], secondLine: words[1]};
    }

    // For 3+ words: split near the midpoint for balanced lines
    const mid = Math.ceil(words.length / 2);
    const firstLine = words.slice(0, mid).join(' ');
    const secondLine = words.slice(mid).join(' ');
    return {firstLine, secondLine};
  };

  return (
    <div className="flex gap-0.5 w-full px-1 pt-4 mb-4 sm:gap-2 sm:px-4 sm:pt-6 sm:mb-6 md:gap-4 md:px-6">
      {Tabs.map(item => {
        return (
          <button
            key={item.type}
            onClick={() => onTabChange(item.type)}
            className={cn(
              // Base
              'flex-1 rounded-lg transition-all duration-200 border border-ui_input_border_dark',
              // vertical layout to allow text wrapping when needed
              'flex flex-col items-center justify-center text-center',
              'text-[9px] px-1 py-1 min-w-0 leading-tight',

              // small screen sizing
              'min-[320px]:h-10 min-[320px]:text-[9px]',
              'min-[375px]:h-11 min-[375px]:text-[10px]',
              'min-[414px]:h-12 min-[414px]:text-[11px]',
              'min-[480px]:h-12 min-[480px]:text-xs',

              // larger screens - allow wrapping when needed
              'sm:h-12 sm:text-sm sm:px-3 sm:py-3',
              'md:h-14 md:text-base md:px-4 md:py-3',
              'lg:h-14 lg:text-lg lg:px-4 lg:py-3',
              'xl:h-14 xl:text-xl xl:px-4 xl:py-3',

              // active/inactive
              activeTab === item.type
                ? 'bg-white text-gray-800 border-gray-300 shadow-sm font-FigtreeBold font-bold'
                : 'bg-gradient-to-tr from-[#DBF1FC] to-[#CBEBFB] text-gray-500 border-none hover:from-[#C7E8F8] hover:to-[#B8E3F6] font-FigtreeMedium',
            )}>
            <span className="text-center leading-tight bg-gradient-to-r from-ui_blue to-ui_blue_light bg-clip-text text-transparent break-words whitespace-normal">
              {item.text}
            </span>
          </button>
        );
      })}
    </div>
  );
};
